import React, { useEffect, useRef, useState } from 'react';
import { InputAdornment, TextField } from '@mui/material';
import { tokens } from '../../theme';

/**
 * Inline numeric stock editor for raw ingredients.
 * Shows packaging as a suffix; saves on blur / Enter.
 */
export default function UpdateStockCount({
  id,
  value,
  packaging,
  threshold = 0,
  onSave,
}) {
  const [editValue, setEditValue] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  const editValueRef = useRef(editValue);
  editValueRef.current = editValue;

  useEffect(() => {
    if (!saving) {
      setEditValue(value ?? '');
    }
  }, [value, saving]);

  const numeric = Number(editValue);
  const stockNum = Number.isFinite(numeric) ? numeric : 0;
  const par = Number(threshold) || 0;
  const warn = par * 1.05;

  let tone = {
    color: tokens.danger,
    bg: tokens.dangerBg,
  };
  if (par <= 0 || stockNum > warn) {
    tone = { color: tokens.accent, bg: tokens.okBg };
  } else if (stockNum > par) {
    tone = { color: tokens.warn, bg: tokens.warnBg };
  }

  const commit = async () => {
    const raw = editValueRef.current;
    const next = Number(raw);
    if (!Number.isFinite(next) || next < 0) {
      setEditValue(value ?? '');
      return;
    }
    if (Number(value) === next) return;
    setSaving(true);
    try {
      await onSave?.(id, next);
    } finally {
      setSaving(false);
    }
  };

  const unitLabel = packaging
    ? (stockNum === 1 ? packaging : `${packaging}s`)
    : '';

  return (
    <TextField
      value={editValue}
      onChange={(e) => setEditValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.target.blur();
        }
        e.stopPropagation();
      }}
      onClick={(e) => e.stopPropagation()}
      type="number"
      size="small"
      disabled={saving}
      inputProps={{ min: 0, step: 'any', inputMode: 'decimal' }}
      InputProps={{
        endAdornment: unitLabel ? (
          <InputAdornment position="end">
            <span style={{ fontSize: 12, fontWeight: 600, color: tone.color }}>
              {unitLabel}
            </span>
          </InputAdornment>
        ) : null,
      }}
      sx={{
        width: '100%',
        maxWidth: 140,
        '& .MuiOutlinedInput-root': {
          height: 34,
          fontSize: 13,
          fontWeight: 650,
          backgroundColor: tone.bg,
          color: tone.color,
          borderRadius: '8px',
          '& fieldset': { borderColor: 'transparent' },
          '&:hover fieldset': { borderColor: tokens.border },
          '&.Mui-focused fieldset': { borderColor: tokens.accent },
        },
        '& input': {
          py: 0.75,
          px: 1,
          color: tone.color,
        },
        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
          WebkitAppearance: 'none',
          margin: 0,
        },
        '& input[type=number]': {
          MozAppearance: 'textfield',
        },
      }}
    />
  );
}
